import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import "@pcp/core/extensions";
import { BehaviorSubject, finalize, Observable } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthServiceFacade } from "../services/auth-service-facade.service";
import { PcpAuthService } from "../services/pcp-auth.service";
import { AUTH_INTERCEPTOR_IGNORE_DOMAINS } from "../injection-tokens";

/**
 * Error messages from `/token/refresh`
 */
enum UnauthorizedErrorMessages {
    REFRESH_TOKEN_EXPIRED = 'Refresh token expired',
    REFRESH_TOKEN_HAS_ALREADY_BEEN_USED = 'Refresh token has already been used',
    REFRESH_TOKEN_IS_INVALID = 'Invalid token',

    REFRESH_TOKEN_NOT_FOUND = 'Invalid token',
}

const TokenExpiredHeader = 'X-Token-Expired';

/**
 * Login and refresh must not carry an access token. `/api/auth/vdo` only shares the prefix - it
 * needs an authenticated caller and answers 401 without the header.
 */
const isSessionEstablishingRequest = (url: string): boolean =>
    url.includes('/api/auth') && !url.includes('/api/auth/vdo');

@Injectable({
    providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {

    private readonly auth = inject(PcpAuthService);
    private readonly ignoredDomains = inject(AUTH_INTERCEPTOR_IGNORE_DOMAINS);


    constructor(
        private readonly authService: AuthServiceFacade,
    ) {
    }

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        if (isSessionEstablishingRequest(request.url)
            || request.url.startsWith('/assets')
            || this.ignoredDomains.some(domain => request.url.includes(domain))
        ) {
            return next.handle(request);
        }

        const token = this.auth.getAuthToken();
        if (token) {
            request = this.addToken(request, token);
        }

        return next.handle(request).pipe(catchError(error => {
            if (error instanceof HttpErrorResponse && error.status === 401) {
                return this.handle401Error(request, next, error);
            } else {
                throw error;
            }
        }));
    }

    private addToken(request: HttpRequest<any>, token: string) {
        return request.clone({
            setHeaders: {
                Authorization: `Bearer ${token}`
            }
        });
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler, error: Error): Observable<HttpEvent<unknown>> {
        if (!this.auth.canRefreshToken()) {
            throw error;
        }

        return this.auth.refreshToken().pipe(
            switchMap(response => next.handle(this.addToken(request, response.token))),
            catchError(nextError => {
                throw nextError;
            })
        );
    }
}
